import { useEffect, useRef } from 'react';

export const VolumeMeter = ({ audioStream }: { audioStream: MediaStream | null }) => {
    const worklet = `
    // https://github.com/GoogleChromeLabs/web-audio-samples/blob/main/src/audio-worklet/basic/volume-meter/volume-meter-processor.js

    const SMOOTHING_FACTOR = 0.8;
    const FRAME_PER_SECOND = 30;
    const FRAME_INTERVAL = 1 / FRAME_PER_SECOND;
    
    /**
     *  Measure microphone volume.
     *
     * @class VolumeMeter
     * @extends AudioWorkletProcessor
     */
    class VolumeMeter extends AudioWorkletProcessor {
        constructor() {
            super();
            this._lastUpdate = currentTime;
            this._volume = 0;
        }
    
        calculateRMS(inputChannelData) {
            // Calculate the squared-sum.
            let sum = 0;
            for (let i = 0; i < inputChannelData.length; i++) {
                sum += inputChannelData[i] * inputChannelData[i];
            }
    
            // Calculate the RMS level and update the volume.
            let rms = Math.sqrt(sum / inputChannelData.length);
            this._volume = Math.max(rms, this._volume * SMOOTHING_FACTOR);
        }
    
        process(inputs, outputs) {
            try {
                const inputChannelData = inputs[0][0];
        
                // Post a message to the node every 16ms.
                if (currentTime - this._lastUpdate > FRAME_INTERVAL) {
                    this.calculateRMS(inputChannelData);
                    this.port.postMessage(this._volume);
                    this._lastUpdate = currentTime;
                }
            } catch {
                this.port.postMessage(0);
            }
    
            return true;
        }
    }
    
    registerProcessor('volume-meter', VolumeMeter);
    `;

    const audioLevelRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!audioStream) {
            return;
        }

        const audioContext = new AudioContext();
        audioContext.audioWorklet.addModule(URL.createObjectURL(new Blob([worklet], { type: 'application/javascript' }))).then(() => {
            if (!audioContext.state || audioContext.state === 'closed') {
                // This can happen if we switch streams too fast
                return;
            }
            const node = new AudioWorkletNode(audioContext, 'volume-meter', {
                parameterData: {
                    clipLevel: 0.9,
                    averaging: 0.5,
                    clipLag: 750
                }
            });
            node.port.onmessage = (event) => {
                let volume = event.data * 1200;
                if (volume > 100) volume = 100;

                const invertVolume = 100 - volume;
                const maskPercentage = Math.round(invertVolume / 10) * 10;
                audioLevelRef.current?.style.setProperty('--_s-levelmask', `${maskPercentage}%`);
            };

            audioContext.createMediaStreamSource(audioStream).connect(node).connect(audioContext.destination);
        });

        return () => {
            audioContext.close();
        };
    }, [audioStream]);

    return (
        <>
            <div className="absolute start-0 bottom-0 w-[8px] h-[min(calc(100%-4px),24rem)] bg-black/90 rounded-[2px] m-2px p-2px">
                <div className="audiometer-inner" ref={audioLevelRef}></div>
            </div>
        </>
    );
};
